<script setup lang="ts">
import type { ForecastScenario } from '../internal/types.ts'
import { Card, EmptyState, ErrorState, IconButton, MenuItem, MenuPopover, NavIcon, Skeleton } from '@taler/ui'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'

defineProps<{
  scenarios: readonly ForecastScenario[]
  isLoading: boolean
  error: Error | null
  retrying: boolean
  deletingId: string | undefined
}>()
const emit = defineEmits<{ retry: []; delete: [scenarioId: string] }>()

const { t } = useI18n()
</script>

<template>
  <div>
    <ul v-if="isLoading" aria-hidden="true" class="flex flex-col gap-3 px-3 py-2">
      <li v-for="index in 3" :key="index" class="flex items-center gap-3">
        <Skeleton width="40%" height="1rem" />
      </li>
    </ul>
    <p v-if="isLoading" class="sr-only" role="status">{{ t('forecast.loading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('forecast.errorTitle')"
      :body="t('forecast.errorBody')"
      :retry-label="t('forecast.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState
      v-else-if="scenarios.length === 0"
      :title="t('forecast.emptyTitle')"
      :body="t('forecast.emptyBody')"
    >
      <template #icon><NavIcon name="planning" /></template>
    </EmptyState>

    <ul v-else class="flex flex-col gap-[var(--space-cell)]">
      <li v-for="scenario in scenarios" :key="scenario.id">
        <Card class="flex min-w-0 items-center gap-[var(--space-cell)]">
          <span
            aria-hidden="true"
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:color-mix(in_srgb,var(--color-accent)_12%,transparent)] text-[color:var(--color-accent)]"
          >
            <NavIcon name="planning" />
          </span>

          <RouterLink
            :to="{ name: 'forecast-scenario', params: { scenarioId: scenario.id } }"
            class="min-w-0 flex-1 truncate text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)] underline decoration-transparent underline-offset-4 transition-colors duration-[var(--motion-fast)] hover:decoration-current"
          >
            {{ scenario.name }}
          </RouterLink>

          <MenuPopover side="bottom" align="end">
            <template #trigger>
              <IconButton
                :disabled="deletingId === scenario.id"
                :aria-label="`${t('forecast.actionsLabel')} ${scenario.name}`"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
                  <circle cx="8" cy="3.5" r="1.4" />
                  <circle cx="8" cy="8" r="1.4" />
                  <circle cx="8" cy="12.5" r="1.4" />
                </svg>
              </IconButton>
            </template>
            <MenuItem @select="emit('delete', scenario.id)">
              {{ deletingId === scenario.id ? t('forecast.deleting') : t('forecast.deleteAction') }}
            </MenuItem>
          </MenuPopover>
        </Card>
      </li>
    </ul>
  </div>
</template>
